import {
  UserIcon,
  ShoppingBagIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  TagIcon,
  GlobeAltIcon,
  MapIcon,
  MapPinIcon,
  ShieldCheckIcon,
  CubeIcon,
  CreditCardIcon,
  TicketIcon,
  TruckIcon,
  Squares2X2Icon,
  Cog6ToothIcon,
  BriefcaseIcon,
  BookOpenIcon,
  BellAlertIcon,
  ClockIcon,
  MegaphoneIcon,
  UserGroupIcon,
  WalletIcon,
  CodeBracketIcon,
  DocumentTextIcon,
  ServerIcon,
  ServerStackIcon,
  LightBulbIcon,
  CalculatorIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  VideoCameraIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  CpuChipIcon,
  CircleStackIcon,
  CloudIcon,
  CloudArrowUpIcon,
  DocumentDuplicateIcon,
  PencilSquareIcon,
  Bars3Icon,
  CurrencyDollarIcon,
  ChartPieIcon,
  HeartIcon,
  TrophyIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

import type { CardDefinition, DashboardStats, HeroIcon } from '../types/adminDashboard';

export const EMPTY_DASHBOARD_STATS: DashboardStats = {
  users: 0,
  products: 0,
  companies: 0,
  loading: true
};

export function getAdminCardDictionary(stats: DashboardStats): Record<string, CardDefinition> {
  return {
    'User Management': {
      name: 'User Management',
      value: stats.loading ? '...' : stats.users,
      icon: UserIcon,
      iconKey: 'UserIcon',
      link: '/admin/user-management',
      color: 'bg-blue-500'
    },
    'App Store': {
      name: 'App Store',
      value: 'Ecosystem',
      icon: CubeIcon,
      iconKey: 'CubeIcon',
      link: '/admin/app-store',
      color: 'bg-indigo-600'
    },
    'Point of Sale': {
      name: 'Point of Sale',
      value: 'Retail',
      icon: DeviceTabletIcon,
      iconKey: 'DeviceTabletIcon',
      link: '/admin/pos',
      color: 'bg-emerald-500'
    },
    'User Submissions': {
      name: 'User Submissions',
      value: 'Manage',
      icon: LightBulbIcon,
      iconKey: 'LightBulbIcon',
      link: '/admin/user-ideas',
      color: 'bg-yellow-500'
    },
    'User Status': {
      name: 'User Status',
      value: 'Manage',
      icon: UserGroupIcon,
      iconKey: 'UserGroupIcon',
      link: '/admin/user-status',
      color: 'bg-teal-500'
    },
    'Projects': {
      name: 'Projects',
      value: 'Workspace',
      icon: BriefcaseIcon,
      iconKey: 'BriefcaseIcon',
      link: '/admin/projects',
      color: 'bg-blue-600'
    },
    'Idea Flow': {
      name: 'Idea Flow',
      value: 'AI Pipeline',
      icon: CodeBracketIcon,
      iconKey: 'CodeBracketIcon',
      link: '/admin/idea-flow',
      color: 'bg-purple-500'
    },
    'Cost Model Profiles': {
      name: 'Cost Model Profiles',
      value: 'Pricing Tiers',
      icon: CodeBracketIcon,
      iconKey: 'CodeBracketIcon',
      link: '/admin/cost-model-profiles',
      color: 'bg-indigo-500'
    },
    'Listings': {
      name: 'Listings',
      value: 'Manage',
      icon: ShoppingBagIcon,
      iconKey: 'ShoppingBagIcon',
      link: '/admin/listings',
      color: 'bg-slate-500'
    },
    'Shipping': {
      name: 'Shipping',
      value: 'Manage',
      icon: TruckIcon,
      iconKey: 'TruckIcon',
      link: '/admin/shipping',
      color: 'bg-amber-600'
    },
    'Carriers': {
      name: 'Carriers',
      value: 'Manage',
      icon: TruckIcon,
      iconKey: 'TruckIcon',
      link: '/admin/shipping-carriers',
      color: 'bg-orange-600'
    },
    'Services': {
      name: 'Services',
      value: 'Manage',
      icon: BriefcaseIcon,
      iconKey: 'BriefcaseIcon',
      link: '/admin/services',
      color: 'bg-indigo-600'
    },
    'Types Management': {
      name: 'Types Management',
      value: 'Manage',
      icon: Squares2X2Icon,
      iconKey: 'Squares2X2Icon',
      link: '/admin/types',
      color: 'bg-purple-600'
    },
    'Systems Control': {
      name: 'Systems Control',
      value: 'Monitor',
      icon: ServerIcon,
      iconKey: 'ServerIcon',
      link: '/admin/system-controls',
      color: 'bg-emerald-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'Server Manager': {
      name: 'Server Manager',
      value: 'Manage',
      icon: ServerIcon,
      iconKey: 'ServerIcon',
      link: '/admin/server-manager',
      color: 'bg-blue-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'Analytics': {
      name: 'Analytics',
      value: 'View',
      icon: ChartBarIcon,
      iconKey: 'ChartBarIcon',
      link: '/admin/analytics',
      color: 'bg-orange-500'
    },
    'System Analytics': {
      name: 'System Analytics',
      value: 'View',
      icon: ChartBarIcon,
      iconKey: 'ChartBarIcon',
      link: '/admin/system-controls?tab=system-analytics',
      color: 'bg-indigo-500'
    },
    'Login Management': {
      name: 'Login Management',
      value: 'Manage',
      icon: ShieldCheckIcon,
      iconKey: 'ShieldCheckIcon',
      link: '/admin/login-security',
      color: 'bg-red-600'
    },
    'Compliance Portal': {
      name: 'Compliance Portal',
      value: 'View',
      icon: ShieldCheckIcon,
      iconKey: 'ShieldCheckIcon',
      link: '/admin/compliance',
      color: 'bg-emerald-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'Plesk Management': {
      name: 'Plesk Management',
      value: 'Manage',
      icon: ServerStackIcon,
      iconKey: 'ServerStackIcon',
      link: '/admin/plesk',
      color: 'bg-indigo-600'
    },
    'Blockchain Management': {
      name: 'Blockchain Management',
      value: 'Manage',
      icon: WalletIcon,
      iconKey: 'WalletIcon',
      link: '/admin/crypto-dashboard',
      color: 'bg-cyan-500'
    },
    'Commodity Trading': {
      name: 'Commodity Trading',
      value: 'Manage',
      icon: CurrencyDollarIcon,
      iconKey: 'CurrencyDollarIcon',
      link: '/admin/commodity-marketplace',
      color: 'bg-amber-500'
    },
    'Investment Pools': {
      name: 'Investment Pools',
      value: 'Manage',
      icon: ChartPieIcon,
      iconKey: 'ChartPieIcon',
      link: '/admin/investment-pools',
      color: 'bg-blue-600'
    },
    'Inactive Companies': {
      name: 'Inactive Companies',
      value: 'View',
      icon: ExclamationTriangleIcon,
      iconKey: 'ExclamationTriangleIcon',
      link: '/admin/companies/inactive',
      color: 'bg-red-500'
    },
    'Products': {
      name: 'Products',
      value: stats.loading ? '...' : stats.products,
      icon: ShoppingBagIcon,
      iconKey: 'ShoppingBagIcon',
      link: '/admin/products',
      color: 'bg-green-500'
    },
    'Total Companies': {
      name: 'Total Companies',
      value: stats.loading ? '...' : stats.companies,
      icon: BuildingOfficeIcon,
      iconKey: 'BuildingOfficeIcon',
      link: '/admin/companies',
      color: 'bg-purple-500'
    },
    'Roles': {
      name: 'Roles',
      value: 'Manage',
      icon: ShieldCheckIcon,
      iconKey: 'ShieldCheckIcon',
      link: '/admin/roles',
      color: 'bg-violet-500'
    },
    'Inventory': {
      name: 'Inventory',
      value: 'Manage',
      icon: CubeIcon,
      iconKey: 'CubeIcon',
      link: '/admin/inventory',
      color: 'bg-emerald-500'
    },
    'Payments': {
      name: 'Payments',
      value: 'Manage',
      icon: CreditCardIcon,
      iconKey: 'CreditCardIcon',
      link: '/admin/payments',
      color: 'bg-lime-500'
    },
    'Coupons': {
      name: 'Coupons',
      value: 'Manage',
      icon: TicketIcon,
      iconKey: 'TicketIcon',
      link: '/admin/coupons',
      color: 'bg-rose-500'
    },
    'Suppliers': {
      name: 'Suppliers',
      value: 'Manage',
      icon: TruckIcon,
      iconKey: 'TruckIcon',
      link: '/admin/suppliers',
      color: 'bg-sky-500'
    },
    'Strains': {
      name: 'Strains',
      value: 'Manage',
      icon: TagIcon,
      iconKey: 'TagIcon',
      link: '/admin/strains',
      color: 'bg-emerald-600'
    },
    'Deals': {
      name: 'Deals',
      value: 'Manage',
      icon: MegaphoneIcon,
      iconKey: 'MegaphoneIcon',
      link: '/admin/deals',
      color: 'bg-red-500'
    },
    'Knowledge Base': {
      name: 'Knowledge Base',
      value: 'Manage',
      icon: BookOpenIcon,
      iconKey: 'BookOpenIcon',
      link: '/admin/knowledge-base',
      color: 'bg-slate-600'
    },
    'Learning Management': {
      name: 'Learning Management',
      value: 'Manage',
      icon: BookOpenIcon,
      iconKey: 'BookOpenIcon',
      link: '/admin/courses',
      color: 'bg-indigo-600'
    },
    'LMS Settings': {
      name: 'LMS Settings',
      value: 'Configure',
      icon: Cog6ToothIcon,
      iconKey: 'Cog6ToothIcon',
      link: '/admin/lms-settings',
      color: 'bg-slate-600'
    },
    'ACL Management': {
      name: 'ACL Management',
      value: 'Access Control',
      icon: ShieldCheckIcon,
      iconKey: 'ShieldCheckIcon',
      link: '/admin/acl',
      color: 'bg-indigo-600'
    },
    'Memberships': {
      name: 'Memberships',
      value: 'Manage',
      icon: UserGroupIcon,
      iconKey: 'UserGroupIcon',
      link: '/admin/memberships',
      color: 'bg-fuchsia-500'
    },
    'Investors': {
      name: 'Investor Relations',
      value: 'Review',
      icon: BriefcaseIcon,
      iconKey: 'BriefcaseIcon',
      link: '/admin/investor-hub',
      color: 'bg-blue-600'
    },
    'Real Estate': {
      name: 'Real Estate & RWA Hub',
      value: 'Manage',
      icon: BuildingOfficeIcon,
      iconKey: 'BuildingOfficeIcon',
      link: '/admin/real-estate',
      color: 'bg-cyan-500'
    },
    'Special Events': {
      name: 'Special Events Hub',
      value: 'Manage',
      icon: TicketIcon,
      iconKey: 'TicketIcon',
      link: '/admin/special-events',
      color: 'bg-purple-500'
    },
    'Sports': {
      name: 'Sports Hub',
      value: 'Manage',
      icon: TrophyIcon,
      iconKey: 'TrophyIcon',
      link: '/admin/sports',
      color: 'bg-emerald-500'
    },
    'KYC Providers': {
      name: 'KYC Providers',
      value: 'Manage',
      icon: ShieldCheckIcon,
      iconKey: 'ShieldCheckIcon',
      link: '/admin/kyc-providers',
      color: 'bg-emerald-600'
    },
    'Mail': {
      name: 'Mail',
      value: 'Manage',
      icon: EnvelopeIcon,
      iconKey: 'EnvelopeIcon',
      link: '/admin/mail-providers',
      color: 'bg-blue-600'
    },
    'SMS': {
      name: 'SMS',
      value: 'Manage',
      icon: DevicePhoneMobileIcon,
      iconKey: 'DevicePhoneMobileIcon',
      link: '/admin/sms-providers',
      color: 'bg-green-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'SIP': {
      name: 'SIP Providers',
      value: 'Manage',
      icon: PhoneIcon,
      iconKey: 'PhoneIcon',
      link: '/admin/sip-providers',
      color: 'bg-teal-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'Bot Settings': {
      name: 'Bot Settings',
      value: 'Manage',
      icon: BellAlertIcon,
      iconKey: 'BellAlertIcon',
      link: '/admin/settings/bot',
      color: 'bg-yellow-500'
    },
    'Unfinished Orders': {
      name: 'Unfinished Orders',
      value: 'Configure',
      icon: ClockIcon,
      iconKey: 'ClockIcon',
      link: '/admin/settings/unfinished-orders',
      color: 'bg-gray-600'
    },
    'Docker': {
      name: 'Docker',
      value: 'Manage',
      icon: CubeIcon,
      iconKey: 'CubeIcon',
      link: '/admin/docker',
      color: 'bg-cyan-600'
    },
    'Document Management': {
      name: 'Document Management',
      value: 'Manage',
      icon: DocumentTextIcon,
      iconKey: 'DocumentTextIcon',
      link: '/admin/documents',
      color: 'bg-indigo-600'
    },
    'Cloud Storage': {
      name: 'Cloud Storage',
      value: 'Manage',
      icon: CloudArrowUpIcon,
      iconKey: 'CloudArrowUpIcon',
      link: '/admin/cloud-storage',
      color: 'bg-cyan-600'
    },
    'E-Signature': {
      name: 'E-Signature',
      value: 'Sign',
      icon: PencilSquareIcon,
      iconKey: 'PencilSquareIcon',
      link: '/admin/esignature',
      color: 'bg-emerald-600'
    },
    'Template Manager': {
      name: 'Template Manager',
      value: 'Manage',
      icon: DocumentDuplicateIcon,
      iconKey: 'DocumentDuplicateIcon',
      link: '/admin/template-manager',
      color: 'bg-teal-600'
    },
    'Legal Management': {
      name: 'Legal Management',
      value: 'Manage',
      icon: BookOpenIcon,
      iconKey: 'BookOpenIcon',
      link: '/admin/legal-management',
      color: 'bg-indigo-700'
    },
    'CRM Management': {
      name: 'CRM Management',
      value: 'Manage',
      icon: UserGroupIcon,
      iconKey: 'UserGroupIcon',
      link: '/crm',
      color: 'bg-purple-600'
    },
    'Workflow Designer': {
      name: 'Workflow Designer',
      value: 'Design',
      icon: CodeBracketIcon,
      iconKey: 'CodeBracketIcon',
      link: '/admin/workflow-designer',
      color: 'bg-purple-500',
      requiredRole: 'SUPER_USER'
    },
    'Backup Management': {
      name: 'Backup Management',
      value: 'Manage',
      icon: ServerIcon,
      iconKey: 'ServerIcon',
      link: '/admin/backup-management',
      color: 'bg-green-600'
    },
    'AI Management': {
      name: 'AI Management',
      value: 'Configure',
      icon: ServerIcon,
      iconKey: 'ServerIcon',
      link: '/admin/ai-management',
      color: 'bg-purple-600'
    },
    'Daily Quotes': {
      name: 'Daily Quotes',
      value: 'Manage',
      icon: BookOpenIcon,
      iconKey: 'BookOpenIcon',
      link: '/admin/daily-quotes',
      color: 'bg-amber-600'
    },
    'Accounting Management': {
      name: 'Accounting Management',
      value: 'Manage',
      icon: CalculatorIcon,
      iconKey: 'CalculatorIcon',
      link: '/admin/accounting-management',
      color: 'bg-blue-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'Commodity Marketplace': {
      name: 'Commodity Marketplace',
      value: 'Manage',
      icon: ChartBarIcon,
      iconKey: 'ChartBarIcon',
      link: '/admin/commodity-marketplace',
      color: 'bg-emerald-600'
    },
    'HR Management': {
      name: 'HR Management',
      value: 'Manage',
      icon: UserGroupIcon,
      iconKey: 'UserGroupIcon',
      link: '/admin/hr-management',
      color: 'bg-purple-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'Navbar Editor': {
      name: 'Navbar Editor',
      value: 'Configure',
      icon: Bars3Icon,
      iconKey: 'Bars3Icon',
      link: '/admin/navbar-editor/admin',
      color: 'bg-indigo-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'Sidebar Editor': {
      name: 'Sidebar Editor',
      value: 'Configure',
      icon: Bars3Icon,
      iconKey: 'Bars3Icon',
      link: '/admin/sidebar-editor/user',
      color: 'bg-purple-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'Website Manager': {
      name: 'Website Manager',
      value: 'Manage',
      icon: GlobeAltIcon,
      iconKey: 'GlobeAltIcon',
      link: '/admin/website-manager',
      color: 'bg-indigo-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'Website Builder': {
      name: 'Website Builder',
      value: 'Build',
      icon: CodeBracketIcon,
      iconKey: 'CodeBracketIcon',
      link: '/admin/website-builder/ai',
      color: 'bg-purple-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'Domain Management': {
      name: 'Domain Management',
      value: 'Manage',
      icon: GlobeAltIcon,
      iconKey: 'GlobeAltIcon',
      link: '/admin/domains',
      color: 'bg-teal-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'Landing CMS': {
      name: 'Landing CMS',
      value: 'Manage',
      icon: GlobeAltIcon,
      iconKey: 'GlobeAltIcon',
      link: '/admin/landing-cms',
      color: 'bg-purple-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'CMS': {
      name: 'Landing CMS',
      value: 'Manage',
      icon: GlobeAltIcon,
      iconKey: 'GlobeAltIcon',
      link: '/admin/landing-cms',
      color: 'bg-purple-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'Support Tickets': {
      name: 'Support Tickets',
      value: 'Manage',
      icon: TicketIcon,
      iconKey: 'TicketIcon',
      link: '/admin/support-tickets',
      color: 'bg-indigo-500',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'Video Conference': {
      name: 'Video Conference',
      value: 'Manage',
      icon: VideoCameraIcon,
      iconKey: 'VideoCameraIcon',
      link: '/admin/video-conferences',
      color: 'bg-purple-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'Janus Management': {
      name: 'Janus Management',
      value: 'Manage',
      icon: ServerIcon,
      iconKey: 'ServerIcon',
      link: '/admin/janus-management',
      color: 'bg-indigo-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'Janus Admin': {
      name: 'Janus Admin',
      value: 'Monitor',
      icon: ChartBarIcon,
      iconKey: 'ChartBarIcon',
      link: '/admin/janus-admin',
      color: 'bg-teal-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'Janus Transcription': {
      name: 'Janus Transcription',
      value: 'Manage',
      icon: DocumentTextIcon,
      iconKey: 'DocumentTextIcon',
      link: '/admin/janus-transcription',
      color: 'bg-violet-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'Log Viewer': {
      name: 'Log Viewer',
      value: 'View',
      icon: DocumentTextIcon,
      iconKey: 'DocumentTextIcon',
      link: '/admin/log-viewer',
      color: 'bg-slate-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'Chat Management': {
      name: 'Chat Management',
      value: 'Manage',
      icon: ChatBubbleLeftRightIcon,
      iconKey: 'ChatBubbleLeftRightIcon',
      link: '/admin/chat-management',
      color: 'bg-blue-600',
      requiredRole: 'ADMIN_OR_STAFF'
    },
    'Financial Services': {
      name: 'Financial Services',
      value: 'Manage',
      icon: CurrencyDollarIcon,
      iconKey: 'CurrencyDollarIcon',
      link: '/admin/commodity-marketplace',
      color: 'bg-emerald-600'
    },
    'Automotive': {
      name: 'Automotive',
      value: 'Manage',
      icon: TruckIcon,
      iconKey: 'TruckIcon',
      link: '/admin/automotive',
      color: 'bg-amber-600'
    },
    'Lifestyle Planning': {
      name: 'Lifestyle Planning',
      value: 'Manage',
      icon: HeartIcon,
      iconKey: 'HeartIcon',
      link: '/admin/lifestyle',
      color: 'bg-cyan-500'
    },
    'Developer Settings': {
      name: 'Developer Settings',
      value: 'Configure',
      icon: CodeBracketIcon,
      iconKey: 'CodeBracketIcon',
      link: '/admin/developer',
      color: 'bg-emerald-500',
      requiredRole: 'ADMIN_OR_STAFF'
    }
  };
}

export interface CardStaticData {
  icon: HeroIcon;
  link: string;
  color: string;
}

export function getCardStaticData(): Record<string, CardStaticData> {
  const dictionary = getAdminCardDictionary(EMPTY_DASHBOARD_STATS);
  return Object.fromEntries(
    Object.entries(dictionary).map(([key, card]) => [
      key,
      { icon: card.icon, link: card.link, color: card.color }
    ])
  );
}
